import {
  ActionIcon,
  Button,
  Checkbox,
  Group,
  Popover,
  Select,
  Stack,
  TextInput,
} from '@mantine/core';
import { Plus } from '@phosphor-icons/react';
import sortBy from 'lodash/sortBy';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  useCreateBoardMutation,
  useGetBoardsByRivalIdQuery,
} from 'store/api/boards';

import type { ComboboxItem } from '@mantine/core';
import type { RivalType } from 'api/api.types';
import type { ChangeEvent, ReactNode } from 'react';

export type RivalSelection = {
  checked: boolean;
  boardId?: number;
  status?: 'success' | 'error';
  cardId?: number;
};

type RivalRowTrackingIds = {
  boardSelect?: string;
  laneSelect?: string;
  addLane?: string;
};

type RivalRowProps = {
  rival: RivalType;
  selection: RivalSelection;
  onToggle: () => void;
  onBoardChange: (boardId: number | undefined) => void;
  trackingIds?: RivalRowTrackingIds;
};

export default function RivalRow({
  rival,
  selection,
  onToggle,
  onBoardChange,
  trackingIds,
}: RivalRowProps): ReactNode {
  const { t } = useTranslation(['Card', 'Common']);
  const [createBoard] = useCreateBoardMutation();
  const [isAddingLane, setIsAddingLane] = useState(false);
  const [newLaneName, setNewLaneName] = useState('');
  const [addLaneError, setAddLaneError] = useState('');
  const [isCreating, setIsCreating] = useState(false);

  const {
    data: boardsData,
    isLoading: isBoardsLoading,
    refetch: refetchBoards,
  } = useGetBoardsByRivalIdQuery(
    { path: { rivalId: rival.id } },
    { skip: !selection.checked },
  );

  const boardData = useMemo((): ComboboxItem[] => {
    if (!boardsData) return [];
    // slice(1) to remove the Scratchpad board
    return sortBy(
      boardsData.slice(1).map((b) => ({
        value: b.id.toString(),
        label: b.name,
      })),
      [(item) => item.label.toLowerCase()],
    );
  }, [boardsData]);

  const hasLanes = boardData.length > 0;

  const handleAddLane = useCallback(async () => {
    const trimmed = newLaneName.trim();
    if (!trimmed) return;

    setAddLaneError('');
    setIsCreating(true);
    try {
      const newBoard = await createBoard({
        name: trimmed,
        profileId: rival.profileId,
      }).unwrap();

      await refetchBoards();
      setNewLaneName('');
      setIsAddingLane(false);
      onBoardChange(newBoard.id);
    } catch {
      setAddLaneError(t('Card:actions.copyToBoards.addLaneError'));
    } finally {
      setIsCreating(false);
    }
  }, [
    newLaneName,
    createBoard,
    rival.profileId,
    refetchBoards,
    onBoardChange,
    t,
  ]);

  const isLocked = selection.status === 'success';

  const getStatusColor = (): string | undefined => {
    if (selection.status === 'success') return 'var(--mantine-color-green-6)';
    if (selection.status === 'error') return 'var(--mantine-color-red-6)';
    return undefined;
  };
  const statusColor = getStatusColor();

  return (
    <Stack gap={4}>
      <Checkbox
        checked={selection.checked}
        onChange={onToggle}
        disabled={isLocked}
        label={rival.name}
        styles={{
          label: { cursor: isLocked ? 'default' : 'pointer' },
          input: statusColor
            ? { backgroundColor: statusColor, borderColor: statusColor }
            : undefined,
        }}
        data-tracking-id={trackingIds?.boardSelect}
      />
      <Group gap="xs" ml={32} wrap="nowrap">
        <Select
          searchable
          size="sm"
          placeholder={
            isBoardsLoading
              ? t('Common:status.loading')
              : t('Card:actions.copyToBoards.lanePlaceholder')
          }
          value={selection.boardId?.toString() ?? null}
          data={boardData}
          disabled={
            isLocked || !selection.checked || !hasLanes || isBoardsLoading
          }
          onChange={(value) => onBoardChange(value ? Number(value) : undefined)}
          w={200}
          data-tracking-id={trackingIds?.laneSelect}
        />
        <Popover
          opened={isAddingLane}
          onClose={() => {
            setIsAddingLane(false);
            setAddLaneError('');
            setNewLaneName('');
          }}
          position="right"
          withArrow
        >
          <Popover.Target>
            <ActionIcon
              variant="subtle"
              size="sm"
              disabled={isLocked || !selection.checked}
              onClick={() => setIsAddingLane((v) => !v)}
              aria-label={t('Card:actions.copyToBoards.addLane')}
              data-tracking-id={trackingIds?.addLane}
            >
              <Plus size={16} />
            </ActionIcon>
          </Popover.Target>
          <Popover.Dropdown>
            <Stack gap="xs" w={200}>
              <TextInput
                size="sm"
                placeholder={t('Card:actions.copyToBoards.laneNamePlaceholder')}
                value={newLaneName}
                onChange={(e: ChangeEvent<HTMLInputElement>) => {
                  setNewLaneName(e.currentTarget.value);
                  setAddLaneError('');
                }}
                error={addLaneError || undefined}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    handleAddLane();
                  }
                }}
                data-autofocus
              />
              <Group justify="flex-end" gap="xs">
                <Button
                  size="xs"
                  variant="subtle"
                  color="gray"
                  onClick={() => {
                    setIsAddingLane(false);
                    setAddLaneError('');
                    setNewLaneName('');
                  }}
                >
                  {t('Common:actions.cancel')}
                </Button>
                <Button
                  size="xs"
                  onClick={handleAddLane}
                  loading={isCreating}
                  disabled={!newLaneName.trim()}
                >
                  {t('Common:add')}
                </Button>
              </Group>
            </Stack>
          </Popover.Dropdown>
        </Popover>
      </Group>
    </Stack>
  );
}
